import { useMemo } from 'react';
import { prop, pick } from 'ramda';
import { useFormatter } from 'js/hooks/useFormatter';
import { useUserSelector, useLiveGamesSelector } from 'js/store/hooks';

import * as helpers from '../helpers';
import { LIVE_GAME_COMMON_SEATS_COUNT } from '../constants';
import * as Types from '../types';

export const useLiveGameConfig = (liveLaunchAlias?: string): Types.ILiveGameConfig | null => {
    const formatter = useFormatter();
    const currencyCode = useUserSelector(prop('currencyCode'));
    const { tables, dynamicImages: { timestamp } } = useLiveGamesSelector(pick(['tables', 'dynamicImages']));

    return useMemo(() => {
        if (!liveLaunchAlias) {
            return null;
        }

        const table = tables.find(item => item.launchAlias === liveLaunchAlias);

        if (!table) {
            return null;
        }

        const dataLimits = table.limits?.[0];
        const { status, freeSeats, playerCount, openingDateTime } = table.state || {};
        const { language = '', dynamicImageUrl = '', name = '' } = table.config || {};
        const currencySymbol = formatter.getCurrencySymbol(currencyCode);
        const isCurrencyAtTheLeft = formatter.getIsCurrencySymbolAtTheLeft({ currencyCode });

        let limits = '';

        if (dataLimits?.min !== undefined && dataLimits?.max !== undefined) {
            limits = `${dataLimits.min}-${dataLimits.max}`;
            limits = isCurrencyAtTheLeft ? `${currencySymbol} ${limits}` : `${limits} ${currencySymbol}`;
        }

        const seatsInfo = playerCount !== undefined ? `${playerCount}${freeSeats ? `/${LIVE_GAME_COMMON_SEATS_COUNT}` : ''}` : '';

        return {
            name,
            limits,
            seatsInfo,
            isScheduled: status === 1,
            launchAlias: table.launchAlias,
            language: language.toUpperCase(),
            dealerName: table.dealer?.name || '',
            dynamicImageUrl: helpers.getDynamicImageUrl(dynamicImageUrl, timestamp.toString()),
            openingDateTime: openingDateTime ? formatter.getDateTimeShort(new Date(openingDateTime)) : ''
        };
    }, [tables, liveLaunchAlias, formatter, currencyCode, timestamp]);
};
